import { Injectable, StateKey, TransferState, makeStateKey } from '@angular/core';
import { IEnvironment } from '@gs/shared';

/** Tracks whether the CHANNEL_ID warning has already been emitted in the current process. Module-scoped so it survives TestBed re-instantiation. */
let channelIdWarned = false;

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  constructor(private readonly transferState: TransferState) {
    if (channelIdWarned) return;
    const channelId = this.env.CHANNEL_ID;
    const parsed = parseInt(channelId, 10);
    if (!channelId) {
      console.warn('[EnvironmentService] CHANNEL_ID is not set in the environment configuration. API requests will be sent without a channel header.');
      channelIdWarned = true;
    } else if (!Number.isFinite(parsed) || parsed <= 0) {
      console.warn('[EnvironmentService] CHANNEL_ID is invalid (must be a positive integer). API requests will be sent without a channel header.');
      channelIdWarned = true;
    }
  }

  public get env(): IEnvironment {
    const key: StateKey<IEnvironment> = makeStateKey<IEnvironment>('env');
    return this.transferState.get<IEnvironment>(key, {
      BACKEND_URL: 'shouldBeOverwritten',
      APP_KEY: 'shouldBeOverwritten',
      ENVIRONMENT: 'production',
      BC_APPID: 'shouldBeOverwritten',
      BC_MEMBER_MODULES: 'shouldBeOverwritten',
      BC_MEMBER_NUMBER: 'shouldBeOverwritten',
      BC_PROJECTID: 'shouldBeOverwritten',
      COMMIT_HASH: 'shouldBeOverwritten',
      GOOGLE_MAPS_KEY: 'shouldBeOverwritten',
      CHANNEL_ID: '',
      DEFAULT_CULTURE: '',
      FIXED_CULTURE: '',
      NATIVE_CULTURE: '',
      CULTURES: ''
    });
  }

  /**
   * Returns the maximum quantity allowed for a single article, falling back to a high default
   * when MAX_QUANTITY_ARTICLE is not configured or not a positive number.
   * @returns {number} The effective maximum article quantity.
   */
  public get maxQuantityArticle(): number {
    const configured = this.env.MAX_QUANTITY_ARTICLE;
    return typeof configured === 'number' && configured > 0 ? configured : 999999;
  }

  /**
   * Whether opening times are rendered in the visitor's timezone instead of the store's.
   * Defaults to `false` when USE_VISITOR_TIME_ZONE is not configured.
   * @returns {boolean} True when visitor-timezone rendering is active.
   */
  public get useVisitorTimeZone(): boolean {
    return this.env.USE_VISITOR_TIME_ZONE === true;
  }

  /**
   * Returns the list of active modules defined in BC_MEMBER_MODULES.
   * @returns {string[]} An array of module names.
   */
  get modules(): string[] {
    return this.env.BC_MEMBER_MODULES.split(',').map((m) => m.trim()).filter(Boolean);
  }

  /**
   * Checks whether the current environment has access to any of the given modules.
   * @param modulesToCheck List of module names to check
   */
  public hasAccessToModules(modulesToCheck: string[]): boolean {
    const moduleSet = new Set(this.modules.map((m) => m.toLowerCase()));
    return modulesToCheck.some((m) => moduleSet.has(m.toLowerCase()));
  }
}
